import { Money } from "../../../components/ui/Money";
import { DEFAULT_CURRENCY, DEFAULT_LOCALE } from '../../../lib/locale';
import React, { useState } from 'react';
import { useDefaultRecords } from '../hooks';
import Decimal from 'decimal.js';
import { Link } from '@tanstack/react-router';
import { Button } from '../../../components/ui/Button';
import { Card, CardContent } from '../../../components/ui/Card';
import { PageLoader } from '../../../components/ui/PageLoader';
import { EntityActionMenu } from '../../../components/ui/EntityActionMenu';
import { Eye } from 'lucide-react';

export function DefaultRecordsList() {
  const [status, setStatus] = useState('active');
  const { data: records, isLoading, isError, error, refetch } = useDefaultRecords({ status });
  const defaultList = Array.isArray(records) ? records : (records?.data ?? []);

  if (isLoading) return <PageLoader />;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Defaulted Members</h1>
      </div>

      <div className="flex space-x-4 border-b pb-2">
        <Button
          variant="ghost"
          className={`px-4 py-2 rounded-none bg-transparent hover:bg-transparent ${status === 'active' ? 'border-b-2 border-accent-500 font-bold' : ''}`}
          onClick={() => setStatus('active')}
        >
          Active
        </Button>
        <Button
          variant="ghost"
          className={`px-4 py-2 rounded-none bg-transparent hover:bg-transparent ${status === 'resolved' ? 'border-b-2 border-accent-500 font-bold' : ''}`}
          onClick={() => setStatus('resolved')}
        >
          Resolved
        </Button>
        <Button
          variant="ghost"
          className={`px-4 py-2 rounded-none bg-transparent hover:bg-transparent ${status === 'written_off' ? 'border-b-2 border-accent-500 font-bold' : ''}`}
          onClick={() => setStatus('written_off')}
        >
          Written-off
        </Button>
      </div>

      {isError ? (
        <Card>
          <div className="p-8 text-center">
            <p className="text-sm font-semibold text-danger-600">Failed to load default records</p>
            <p className="text-xs text-text-muted mt-1">{error instanceof Error ? error.message : 'Unknown error'}</p>
            <Button variant="secondary" size="sm" className="mt-3" onClick={() => refetch()}>Retry</Button>
          </div>
        </Card>
      ) : (
        <Card className="overflow-x-auto">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-surface-2">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Customer</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Group</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Default Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Outstanding Principal</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Outstanding Penalty</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-text-secondary uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-surface-1 divide-y divide-border">
              {defaultList.map(record => (
                <tr key={record.id} className="hover:bg-surface-2 cursor-pointer">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Link to={"/defaults/$id" as any} params={{ id: record.id } as any} className="text-accent-500 hover:underline">
                      {record.customerName}
                    </Link>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">{record.groupId}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{record.defaultDate}</td>
                  <td className="px-6 py-4 whitespace-nowrap"><Money paise={record.outstandingPrincipal} currency={DEFAULT_CURRENCY} locale={DEFAULT_LOCALE} /></td>
                  <td className="px-6 py-4 whitespace-nowrap"><Money paise={record.outstandingPenalty} currency={DEFAULT_CURRENCY} locale={DEFAULT_LOCALE} /></td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      record.status === 'active' ? 'bg-danger-bg text-danger-text' :
                      record.status === 'resolved' ? 'bg-success-bg text-success-text' : 'bg-surface-2 text-text-secondary'
                    }`}>
                      {record.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <EntityActionMenu
                      actions={[
                        {
                          label: 'View Default Record',
                          icon: <Eye className="w-4 h-4" />,
                          onClick: () => {
                            window.location.href = `/defaults/${record.id}`;
                          },
                        },
                      ]}
                    />
                  </td>
                </tr>
              ))}
              {defaultList.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-6 py-4 text-center text-text-secondary">No default records found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
